import axios from "@/util/axios";
import {VuexActionContext} from "@/store/index";
import {GetAvailableProductsResponse} from "@/interfaces/httpResponses/GetAvailableProductsResponse";
import {GetPagedDataPackagesResponse} from "@/interfaces/httpResponses/GetPagedDataPackagesResponse";
import {DataPackageFilters, GetDataPackageFiltersResponse} from "@/interfaces/httpResponses/GetDataPackageFiltersResponse";
import {Product} from "@/interfaces/entities/Product";
import {CartItem} from "@/interfaces/entities/CartItem";
import {CartPrice} from "@/interfaces/entities/CartPrice";
import {CartPriceResponse} from "@/interfaces/httpResponses/CartPriceResponse";
import {GetJoinerBonusInfo} from "@/interfaces/httpResponses/GetJoinerBonusInfo";
import {JoinerBonusInfo} from "@/interfaces/entities/JoinerBonusInfo";
import {BulkDiscountInfo} from "@/interfaces/entities/BulkDiscount";
import {GetBulkDiscountInfo} from "@/interfaces/httpResponses/GetBulkDiscountInfo";
import {Price} from "@/interfaces/entities/Price";

/** Query for the paginated + server-filtered data-packages endpoint. */
export interface DataPackagesPageQuery {
    pageIndex?: number;
    pageSize?: number;
    sortBy?: string;
    sortDir?: string;
    countrySets?: string[];
    validityValues?: number[];
    sizeValues?: number[];
    includeUnlimited?: boolean;
    packageCategories?: string[];
}

export interface ProductsState {
    availableProducts: Array<Product>;
    discountRate: number;
    pagedProducts: Array<Product>;
    pageIndex: number;
    pageSize: number;
    totalPageCount: number;
    filterOptions: DataPackageFilters | null;
    filtersLoading: boolean;
}

export const productsState: ProductsState = {
    availableProducts: [],
    discountRate: 0,
    pagedProducts: [],
    pageIndex: 0,
    pageSize: 50,
    totalPageCount: 0,
    filterOptions: null,
    filtersLoading: false
};

export const productsMutations = {
    SET_AVAILABLE_PRODUCTS(state: ProductsState, products: Array<Product>) {
        state.availableProducts = products;
    },

    SET_DISCOUNT_RATE(state: ProductsState, rate: number) {
        state.discountRate = rate;
    },

    SET_PAGED_PRODUCTS(state: ProductsState, products: Array<Product>) {
        state.pagedProducts = products;
    },

    SET_PAGED_META(state: ProductsState, meta: { totalPageCount: number; pageIndex: number; pageSize: number }) {
        state.totalPageCount = meta.totalPageCount;
        state.pageIndex = meta.pageIndex;
        state.pageSize = meta.pageSize;
    },

    SET_FILTER_OPTIONS(state: ProductsState, filterOptions: DataPackageFilters) {
        state.filterOptions = filterOptions;
    },

    SET_FILTERS_LOADING(state: ProductsState, loading: boolean) {
        state.filtersLoading = loading;
    }
};

export const productsActions = {

    async FETCH_JOINER_BONUS_INFO(): Promise<JoinerBonusInfo> {
        const response: GetJoinerBonusInfo = await axios.get("/products/joiner-bonus");
        console.log("[productsStore] Got joiner bonus: ", response.data);
        return response.data;
    },

    async FETCH_BULK_DISCOUNT_INFOS(): Promise<Array<BulkDiscountInfo>> {
        const response: GetBulkDiscountInfo = await axios.get("/payments/bulk-discounts");
        console.log("[productsStore] Got joiner bonus: ", response.data);
        return response.data;
    },

    async FETCH_AVAILABLE_PRODUCTS({commit}: VuexActionContext<Array<Product> | number>) {
        const response: GetAvailableProductsResponse = await axios.get("/products/data-packages");
        console.log("[productsStore] Got products: ", response.data);
        commit("SET_AVAILABLE_PRODUCTS", response.data.dataPackages.map((p: Product) => {
            p.color = "black"; // TODO: replace me!!!
            return p;
        }));
        commit("SET_DISCOUNT_RATE", response.data.discountRate);

    },

    async FETCH_DATA_PACKAGES_PAGE({commit}: VuexActionContext<any>, query: DataPackagesPageQuery = {}) {
        const params: { [key: string]: string | number | boolean } = {
            page_index: query.pageIndex ?? 0,
            page_size: query.pageSize ?? 50
        };
        if (query.sortBy) params.sort_by = query.sortBy;
        if (query.sortDir) params.sort_dir = query.sortDir;
        // Arrays go as comma-separated values; Spring binds them to List<> params.
        if (query.countrySets?.length) params.country_set = query.countrySets.join(",");
        if (query.validityValues?.length) params.validity_value = query.validityValues.join(",");
        if (query.sizeValues?.length) params.size_value = query.sizeValues.join(",");
        if (query.includeUnlimited) params.include_unlimited = true;
        if (query.packageCategories?.length) params.package_category = query.packageCategories.join(",");

        const response: GetPagedDataPackagesResponse = await axios.get("/products/data-packages/paged", {params});
        commit("SET_PAGED_PRODUCTS", response.data.dataPackages.map((p: Product) => {
            p.color = "black"; // TODO: replace me!!!
            return p;
        }));
        commit("SET_PAGED_META", {
            totalPageCount: response.data.totalPageCount,
            pageIndex: response.data.pageIndex,
            pageSize: response.data.pageSize
        });
        commit("SET_DISCOUNT_RATE", response.data.discountRate);
    },

    async FETCH_DATA_PACKAGE_FILTERS({commit}: VuexActionContext<any>) {
        commit("SET_FILTERS_LOADING", true);
        try {
            const response: GetDataPackageFiltersResponse = await axios.get("/products/data-packages/filters");
            commit("SET_FILTER_OPTIONS", response.data);
        } finally {
            commit("SET_FILTERS_LOADING", false);
        }
    },

    async CALCULATE_CART_PRICE(context: VuexActionContext<Array<Price>>, cartItems: Array<CartItem>): Promise<CartPrice> {
        const requestBody = cartItems.map((c: CartItem) => {
            return {
                dataPackageId: c.id,
                amount: c.amount
            };
        });
        const response: CartPriceResponse = await axios.post("/products/calculate-cart-price", requestBody);
        return response.data;
    }
};
